<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Chats', to: ROUTES.ADMIN.CHATS.CHAT_LIST },
            { title: 'Search', to: ROUTES.ADMIN.CHATS.CHAT_SEARCH }
          ]"
        />
      </div>
      <!-- End Page Header -->

      <!-- Search Filters Card -->
      <div class="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 class="mb-4 text-lg font-semibold text-gray-800">Search Chat Sessions</h2>
        <div class="grid gap-4 md:grid-cols-4">
          <!-- Keyword Search -->
          <div class="md:col-span-2">
            <label class="mb-2 block text-sm font-medium text-gray-700">Keyword Search</label>
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="e.g. email, verbessern, schreiben..."
              class="block w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-purple-500
                focus:ring-purple-500"
              @keyup.enter="performSearch"
            />
          </div>

          <!-- Start Date -->
          <div>
            <DatePickerField
              v-model="startDate"
              name="startDate"
              label="From Date"
            />
          </div>

          <!-- End Date -->
          <div>
            <DatePickerField
              v-model="endDate"
              name="endDate"
              label="To Date"
            />
          </div>
        </div>

        <!-- Role Filter Checkboxes -->
        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-gray-700">Search in:</label>
          <div class="flex items-center gap-6">
            <label class="flex cursor-pointer items-center gap-2">
              <input
                v-model="searchInUserPrompts"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span class="text-sm text-gray-700">User prompts</span>
            </label>
            <label class="flex cursor-pointer items-center gap-2">
              <input
                v-model="searchInAiAnswers"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span class="text-sm text-gray-700">AI answers</span>
            </label>
          </div>
        </div>

        <!-- Search Button -->
        <div class="mt-4 flex items-center gap-3">
          <Button
            :disabled="isSearching"
            @click="performSearch"
          >
            <MagnifyingGlassIcon class="mr-2 h-4 w-4" />
            {{ isSearching ? 'Searching...' : 'Search' }}
          </Button>
          <Button
            variant="outline"
            @click="clearFilters"
          >
            Clear Filters
          </Button>
          <span
            v-if="hasSearched"
            class="text-sm text-gray-500"
          >
            {{ resultCount }} chat sessions found
          </span>
        </div>
      </div>

      <!-- Results Table Card -->
      <div
        v-if="hasSearched"
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <div>
          <div class="overflow-x-auto">
            <div class="inline-block min-w-full align-middle">
              <!-- Loading State -->
              <div
                v-if="isSearching"
                class="flex items-center justify-center py-12"
              >
                <div
                  class="h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"
                ></div>
                <span class="ml-3 text-gray-600">Searching...</span>
              </div>

              <!-- Results Table -->
              <Table
                v-else-if="searchResults.length > 0"
                class="min-w-full divide-y divide-gray-200"
              >
                <TableHeader class="bg-gray-50">
                  <TableRow>
                    <TableCell>UUID</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Messages</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow
                    v-for="chatSession in searchResults"
                    :key="chatSession.uuid"
                  >
                    <UniqueTableDataCell :truncate="true">
                      <router-link
                        :to="ROUTES.ADMIN.CHATS.CHAT_DETAIL.replace(':uuid', chatSession.uuid)"
                        class="text-blue-600 hover:text-blue-800"
                      >
                        {{ chatSession.uuid }}
                      </router-link>
                    </UniqueTableDataCell>
                    <TableCell>
                      {{ formatDate(chatSession.created_at) }}
                    </TableCell>
                    <TableCell>
                      {{ chatSession.title || '-' }}
                    </TableCell>
                    <TableCell>
                      {{ chatSession.messages?.length || 0 }}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {{ chatSession.session_type }}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <router-link
                        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                          text-blue-600 hover:text-blue-800"
                        :to="ROUTES.ADMIN.CHATS.CHAT_DETAIL.replace(':uuid', chatSession.uuid)"
                      >
                        View Details
                      </router-link>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <!-- No Results -->
              <div
                v-else
                class="flex flex-col items-center justify-center py-12 text-center"
              >
                <ChatBubbleLeftRightIcon class="h-12 w-12 text-gray-300" />
                <p class="mt-4 text-lg font-medium text-gray-800">No chats found</p>
                <p class="text-sm text-gray-500">Try adjusting your search keyword or date range</p>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div
            v-if="searchResults.length > 0"
            class="mt-4 flex items-center justify-between"
          >
            <p class="text-sm text-gray-600">
              Showing {{ searchResults.length }} of {{ resultCount }} results
            </p>
            <div class="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                :disabled="!hasNextPage"
                @click="goToPage(currentPage + 1)"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Initial State -->
      <div
        v-else
        class="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-12
          text-center shadow-sm"
      >
        <MagnifyingGlassIcon class="h-16 w-16 text-gray-300" />
        <p class="mt-4 text-lg font-medium text-gray-800">Search for Chat Sessions</p>
        <p class="text-sm text-gray-500">
          Enter a keyword and optionally select a date range to find chat sessions
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { type AIChatSession } from '@/types/generated'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { MagnifyingGlassIcon, ChatBubbleLeftRightIcon } from '@heroicons/vue/24/outline'

  // Search state
  const searchKeyword = ref('')
  const startDate = ref<string | null>(null)
  const endDate = ref<string | null>(null)
  const searchInUserPrompts = ref(true)
  const searchInAiAnswers = ref(true)
  const isSearching = ref(false)
  const hasSearched = ref(false)

  // Results state
  const searchResults = ref<AIChatSession[]>([])
  const resultCount = ref(0)
  const currentPage = ref(1)
  const pageSize = 20

  const hasNextPage = computed(() => {
    return currentPage.value * pageSize < resultCount.value
  })

  /**
   * Convert German date format (DD.MM.YYYY) to ISO format (YYYY-MM-DD) for API.
   */
  const convertToISODate = (germanDate: string | null): string | null => {
    if (!germanDate) return null
    const [day, month, year] = germanDate.split('.')
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  /**
   * Perform search by fetching chat sessions from the API.
   */
  const performSearch = async () => {
    if (!searchKeyword.value.trim() && !startDate.value && !endDate.value) {
      return
    }

    isSearching.value = true
    hasSearched.value = true
    currentPage.value = 1

    try {
      await fetchResults()
    } finally {
      isSearching.value = false
    }
  }

  /**
   * Fetch results from the API with current filters.
   */
  const fetchResults = async () => {
    const params = new URLSearchParams()
    params.append('limit', pageSize.toString())
    params.append('offset', ((currentPage.value - 1) * pageSize).toString())

    if (searchKeyword.value.trim()) {
      params.append('search', searchKeyword.value.trim())
    }

    const isoStartDate = convertToISODate(startDate.value)
    const isoEndDate = convertToISODate(endDate.value)

    if (isoStartDate) {
      params.append('start_date', isoStartDate)
    }
    if (isoEndDate) {
      params.append('end_date', isoEndDate)
    }

    // Build search_roles parameter based on checkboxes
    const roles: string[] = []
    if (searchInUserPrompts.value) {
      roles.push('user')
    }
    if (searchInAiAnswers.value) {
      roles.push('assistant')
    }
    if (roles.length > 0 && roles.length < 2) {
      // Only send if filtering (not both selected)
      params.append('search_roles', roles.join(','))
    }

    const url = `/api/v3/ai_chat_sessions/?${params.toString()}`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to search chat sessions: ${response.statusText}`)
    }

    const data = await response.json()
    searchResults.value = data.results || []
    resultCount.value = data.count || 0
  }

  /**
   * Navigate to a specific page.
   */
  const goToPage = async (page: number) => {
    currentPage.value = page
    isSearching.value = true
    try {
      await fetchResults()
    } finally {
      isSearching.value = false
    }
  }

  /**
   * Clear all search filters.
   */
  const clearFilters = () => {
    searchKeyword.value = ''
    startDate.value = null
    endDate.value = null
    searchInUserPrompts.value = true
    searchInAiAnswers.value = true
    searchResults.value = []
    resultCount.value = 0
    hasSearched.value = false
    currentPage.value = 1
  }

  /**
   * Format date for display.
   */
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
</script>

<style scoped></style>
